import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Flex,
  Spinner,
  Text,
  useToast,
  VStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  FormControl,
  FormLabel,
  Input,
  useDisclosure,
  IconButton,
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';

import Header from './Components/KanbanComponents/Header';
import DroppableColumn from './Components/KanbanComponents/DroppableColumn';
import TaskCard from './Components/KanbanComponents/TaskCard';
import CardModal from './Components/KanbanComponents/CardModal';
import socket, { setBoardId, setRefreshCallback } from './utils/socket';

import {
  findContainer,
  fetchListsAndCards,
  fetchCardDetails,
  moveCardAPI,
  handleDragEndLogic
} from './utils/dragUtils';

import { fetchAllBoards } from './ProjectBoardServices/ProjectBoardAPI/ProjectBoardsAPi';

const BoardDetailsPage = () => {
  const { boardId, taskId } = useParams();
  const toast = useToast();

  // --- State ---
  const [columns, setColumns] = useState([]);
  const [users, setUsers] = useState([]);
  const [currentBoard, setCurrentBoard] = useState(null);
  const [boardLoading, setBoardLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activeTask, setActiveTask] = useState(null);
  const activeTaskRef = useRef(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  const [filters, setFilters] = useState({});
  const [tempFilters, setTempFilters] = useState({});
  const { isOpen: isFilterOpen, onOpen: onFilterOpen, onClose: onFilterClose } = useDisclosure();

  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const columnsRef = useRef([]);
  const rafId = useRef(null);
  const hasOpenedUrlTask = useRef(false);
  const scrollContainerRef = useRef(null);
  const autoScrollIntervalRef = useRef(null);
  
  // --- Sensors for DnD ---
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 3 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // --- Sync ref with state ---
  useEffect(() => { columnsRef.current = columns; }, [columns]);

  // --- Check scroll position and show/hide arrows ---
  const checkScrollPosition = useCallback(() => {
    if (!scrollContainerRef.current) return;
    
    const container = scrollContainerRef.current;
    const { scrollLeft, scrollWidth, clientWidth } = container;
    
    setShowLeftArrow(scrollLeft > 10);
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
  }, []);

  useEffect(() => {
    checkScrollPosition();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollPosition);
      window.addEventListener('resize', checkScrollPosition);
      return () => {
        container.removeEventListener('scroll', checkScrollPosition);
        window.removeEventListener('resize', checkScrollPosition);
      };
    }
  }, [checkScrollPosition, columns]);

  // --- Scroll functions ---
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  // --- Fetch Board Details safely ---
  useEffect(() => {
    let isMounted = true;

    const fetchBoardDetailsData = async () => {
      if (!boardId) return;
      try {
        setBoardLoading(true);
        const response = await fetchAllBoards();
        if (!isMounted) return;

        const board = response.data?.data?.find(b => b.id === boardId);
        if (board) {
          setCurrentBoard(board);
          setUsers(board.members || []);
        } else {
          setError('Board not found');
        }
      } catch (err) {
        if (!isMounted) return;
        console.error(err);
        setError('Failed to fetch board details');
        toast({ title: 'Error', description: 'Failed to load board', status: 'error', duration: 3000, isClosable: true });
      } finally {
        if (isMounted) setBoardLoading(false);
      }
    };

    fetchBoardDetailsData();
    return () => { isMounted = false; };
  }, [boardId, toast]);

  // --- Load Columns & Cards safely ---
  const loadData = useCallback(async () => {
    let isMounted = true;
    if (!boardId) return;

    setLoading(true);
    try {
      const { columnsWithCards, error: fetchError } = await fetchListsAndCards(boardId, filters);
      if (!isMounted) return;
      setColumns(columnsWithCards || []);
      setError(fetchError || null);
    } catch (err) {
      if (!isMounted) return;
      console.error(err);
      setError('Failed to fetch columns/cards');
    } finally {
      if (isMounted) setLoading(false);
    }
    return () => { isMounted = false; };
  }, [boardId, filters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // --- Socket: board:refresh pe loadData call karo ---
  useEffect(() => {
    if (boardId) {
      setBoardId(boardId);
      if (socket.connected) {
        socket.emit("board:join", { boardId });
        console.log("📋 board:join emit for boardId:", boardId);
      }
      setRefreshCallback(() => loadData);
    }
    return () => {
      setRefreshCallback(null);
    };
  }, [boardId, loadData]);

  // --- Helper function to find task in columns ---
  const findTaskInColumns = useCallback((taskId) => {
    if (!taskId || !columns.length) return null;
    
    for (const column of columns) {
      if (column.cards && Array.isArray(column.cards)) {
        const task = column.cards.find(card => {
          const cardId = card.id || card.originalId || card._id || card.cardId || card.taskId;
          return cardId === taskId;
        });
        if (task) return task;
      }
    }
    return null;
  }, [columns]);

  // --- Check for taskId in URL and open modal ---
  useEffect(() => {
    if (taskId && columns.length > 0 && !hasOpenedUrlTask.current && !loading) {
      hasOpenedUrlTask.current = true;
      
      const taskInColumns = findTaskInColumns(taskId);
      
      if (taskInColumns) {
        handleTaskClick(taskInColumns, false);
      } else {
        const openTaskFromUrl = async () => {
          setIsModalOpen(true);
          setModalLoading(true);
          
          try {
            const detailedTask = await fetchCardDetails(taskId);
            if (detailedTask) {
              setSelectedTask({ ...detailedTask, id: taskId });
            } else {
              toast({
                title: 'Task Not Found',
                description: 'The requested task could not be found.',
                status: 'warning',
                duration: 3000,
                isClosable: true
              });
              window.history.replaceState(null, '', `/home/ap/board-details/${boardId}`);
            }
          } catch (err) {
            console.error(err);
            toast({
              title: 'Error',
              description: 'Failed to load task details.',
              status: 'error',
              duration: 3000,
              isClosable: true
            });
            window.history.replaceState(null, '', `/home/ap/board-details/${boardId}`);
          } finally {
            setModalLoading(false);
          }
        };
        
        openTaskFromUrl();
      }
    }
  }, [taskId, columns, loading, findTaskInColumns, boardId, toast]);

  // --- Reset flag when taskId changes ---
  useEffect(() => {
    hasOpenedUrlTask.current = false;
  }, [taskId]);

  // --- Task Modal Handlers ---
  const handleTaskClick = async (task, updateUrl = true) => {
    if (!task) return;

    const taskIdForUrl = task.id || task.originalId || task._id || task.cardId || task.taskId;
    if (!taskIdForUrl) return;

    if (updateUrl) window.history.pushState(null, '', `/home/ap/board-details/${boardId}/task/${taskIdForUrl}`);

    setSelectedTask(task);
    setIsModalOpen(true);
    setModalLoading(true);

    let isMounted = true;

    try {
      const detailedTask = await fetchCardDetails(taskIdForUrl);
      if (!isMounted) return;
      if (detailedTask) setSelectedTask(prev => ({ ...prev, ...detailedTask, id: taskIdForUrl }));
    } catch (err) {
      if (!isMounted) return;
      console.error(err);
      toast({ title: 'Error', description: 'Failed to load task details.', status: 'error', duration: 3000, isClosable: true });
    } finally {
      if (!isMounted) return;
      setModalLoading(false);
    }

    return () => { isMounted = false };
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedTask(null);
    hasOpenedUrlTask.current = false;
    window.history.pushState(null, '', `/home/ap/board-details/${boardId}`);
  };

  // --- Drag Handlers ---
  const handleDragStart = useCallback(event => {
    const task = event.active.data.current?.task;
    if (!task) return;
    setActiveTask(task);
    activeTaskRef.current = task;
  }, []);

  const handleDragOver = useCallback((event) => {
    if (!scrollContainerRef.current) return;

    const { clientX } = event.activatorEvent || event;
    if (!clientX) return;

    const container = scrollContainerRef.current;
    const { left, right } = container.getBoundingClientRect();
    const threshold = 150;
    const scrollSpeed = 20;

    if (autoScrollIntervalRef.current) {
      clearInterval(autoScrollIntervalRef.current);
      autoScrollIntervalRef.current = null;
    }

    if (clientX < left + threshold) {
      autoScrollIntervalRef.current = setInterval(() => {
        container.scrollLeft -= scrollSpeed;
      }, 16);
    } else if (clientX > right - threshold) {
      autoScrollIntervalRef.current = setInterval(() => {
        container.scrollLeft += scrollSpeed;
      }, 16);
    }
  }, []);

  const handleDragEnd = useCallback(async event => {
    if (autoScrollIntervalRef.current) {
      clearInterval(autoScrollIntervalRef.current);
      autoScrollIntervalRef.current = null;
    }

    setActiveTask(null);
    const task = event.active.data.current?.task || activeTaskRef.current;
    activeTaskRef.current = null;

    await handleDragEndLogic(event, columnsRef, setColumns, moveCardAPI, toast);
  }, [toast]);

  // --- Filter Handlers ---
  const handleSimpleSearch = (e) => { setFilters(prev => ({ ...prev, q: e.target.value })); };
  const handleFilterOpen = useCallback(() => { setTempFilters({ ...filters }); onFilterOpen(); }, [filters, onFilterOpen]);
  const applyAdvancedFilters = () => { setFilters(tempFilters); onFilterClose(); };
  const resetFilters = () => { setFilters({}); setTempFilters({}); onFilterClose(); };

  // --- Cleanup on unmount ---
  useEffect(() => {
    return () => {
      if (rafId.current) cancelAnimationFrame(rafId.current);
      if (autoScrollIntervalRef.current) clearInterval(autoScrollIntervalRef.current);
    };
  }, []);

  // --- Render ---
  if (!boardId) return <Box minH="100vh" bg="#F4F5F7"><Flex justify="center" align="center" minH="100vh"><Text color="red.500">Invalid board ID</Text></Flex></Box>;
  if (boardLoading) return <Box minH="100vh" bg="#F4F5F7"><Flex justify="center" align="center" minH="100vh"><Spinner size="xl" color="blue.500" /></Flex></Box>;

  return (
    <Box minH="100vh" bg="#F4F5F7">
      <Header
        users={users}
        boardData={currentBoard}
        boardTitle={currentBoard?.title}
        searchValue={filters.q || ''}
        onSearchChange={handleSimpleSearch}
        onFilterOpen={handleFilterOpen}
        onReset={resetFilters}
      />

      <Container maxW="container.xl" py={6}>
        {loading ? (
          <Flex justify="center" align="center" minH="50vh"><Spinner size="xl" color="blue.500" /></Flex>
        ) : error ? (
          <Text color="red.500" textAlign="center">{error}</Text>
        ) : (
          <Box position="relative">
            {/* Left Arrow Button */}
            {showLeftArrow && (
              <IconButton
                icon={<ChevronLeftIcon boxSize={8} />}
                position="absolute"
                left="-20px"
                top="50%"
                transform="translateY(-50%)"
                zIndex={10}
                onClick={scrollLeft}
                colorScheme="blue"
                size="lg"
                borderRadius="full"
                boxShadow="lg"
                aria-label="Scroll left"
                _hover={{ transform: 'translateY(-50%) scale(1.1)' }}
                transition="all 0.2s"
              />
            )}

            {/* Right Arrow Button */}
            {showRightArrow && (
              <IconButton
                icon={<ChevronRightIcon boxSize={8} />}
                position="absolute"
                right="-20px"
                top="50%"
                transform="translateY(-50%)"
                zIndex={10}
                onClick={scrollRight}
                colorScheme="blue"
                size="lg"
                borderRadius="full"
                boxShadow="lg"
                aria-label="Scroll right"
                _hover={{ transform: 'translateY(-50%) scale(1.1)' }}
                transition="all 0.2s"
              />
            )}

            <DndContext 
              sensors={sensors} 
              collisionDetection={closestCenter} 
              onDragStart={handleDragStart} 
              onDragOver={handleDragOver} 
              onDragEnd={handleDragEnd}
            >
              <Flex 
                ref={scrollContainerRef} 
                gap={2} 
                overflowX="auto" 
                pb={4}
                css={{
                  '&::-webkit-scrollbar': {
                    height: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: '#f1f1f1',
                    borderRadius: '10px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#888',
                    borderRadius: '10px',
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    background: '#555',
                  },
                }}
              >
                {columns.map((col, idx) => (
                  <DroppableColumn key={col.id} column={col} onTaskClick={handleTaskClick} isFirstColumn={idx === 0} />
                ))}
              </Flex>
              <DragOverlay>{activeTask && <TaskCard task={activeTask} isDragging />}</DragOverlay>
            </DndContext>
          </Box>
        )}
      </Container>

      <CardModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        task={selectedTask}
        loading={modalLoading}
        columns={columns}
        setColumns={setColumns}
        moveCardAPI={moveCardAPI}
      />

      <Modal isOpen={isFilterOpen} onClose={onFilterClose} isCentered motionPreset="scale">
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(5px)" />
        <ModalContent borderRadius="2xl" p={6} bg="white" boxShadow="lg">
          <ModalHeader fontSize="xl" fontWeight="bold" color="gray.700" display="flex" justifyContent="space-between" alignItems="center">
            Advanced Filters
            <ModalCloseButton
              position="static"
              m={0}
              onClick={onFilterClose}
              _hover={{ bg: 'gray.100', color: 'red.500' }}
              borderRadius="full"
            />
          </ModalHeader>

          <ModalBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel fontWeight="semibold" color="gray.600">Assigned To</FormLabel>
                <Input
                  placeholder="Enter assignee"
                  value={tempFilters.assigned_to || ''}
                  onChange={e => setTempFilters(prev => ({ ...prev, assigned_to: e.target.value }))}
                  borderRadius="lg"
                  focusBorderColor="blue.400"
                  bg="gray.50"
                />
              </FormControl>

              <FormControl>
                <FormLabel fontWeight="semibold" color="gray.600">Members</FormLabel>
                <Input
                  placeholder="Comma separated members"
                  value={(tempFilters.members || []).join(',')}
                  onChange={e => setTempFilters(prev => ({ ...prev, members: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))}
                  borderRadius="lg"
                  focusBorderColor="blue.400"
                  bg="gray.50"
                />
              </FormControl>

              <FormControl>
                <FormLabel fontWeight="semibold" color="gray.600">Labels</FormLabel>
                <Input
                  placeholder="Comma separated labels"
                  value={(tempFilters.labels || []).join(',')}
                  onChange={e => setTempFilters(prev => ({ ...prev, labels: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))}
                  borderRadius="lg"
                  focusBorderColor="blue.400"
                  bg="gray.50"
                />
              </FormControl>

              <FormControl>
                <FormLabel fontWeight="semibold" color="gray.600">Due From</FormLabel>
                <Input
                  type="date"
                  value={tempFilters.due_from || ''}
                  onChange={e => setTempFilters(prev => ({ ...prev, due_from: e.target.value }))}
                  borderRadius="lg"
                  focusBorderColor="blue.400"
                  bg="gray.50"
                />
              </FormControl>

              <FormControl>
                <FormLabel fontWeight="semibold" color="gray.600">Due To</FormLabel>
                <Input
                  type="date"
                  value={tempFilters.due_to || ''}
                  onChange={e => setTempFilters(prev => ({ ...prev, due_to: e.target.value }))}
                  borderRadius="lg"
                  focusBorderColor="blue.400"
                  bg="gray.50"
                />
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter gap={3}>
            <Button colorScheme="blue" borderRadius="lg" onClick={applyAdvancedFilters}>
              Apply Filters
            </Button>
            <Button variant="outline" borderRadius="lg" onClick={onFilterClose}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

    </Box>
  );
};

export default BoardDetailsPage;